<template>
  <div class="polly-pricer-page">
    <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-calculator"></i>
        </div>
        <div>
          <h1 class="page-title">Pricing Engine</h1>
        </div>
      </div>
    </PageHeaderPortal>

    <div v-if="loading" class="state-card">Loading the Pricing Engine...</div>

    <div v-else-if="error" class="blocked-card">
      <div class="blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
      <div>
        <div class="blocked-title">Couldn't open the Pricing Engine</div>
        <div class="blocked-text">{{ error }}</div>
      </div>
    </div>

    <iframe
      v-else
      :src="launchUrl"
      class="polly-pricer-frame"
      title="Pricing Engine"
      allow="clipboard-write"
    ></iframe>
  </div>
</template>

<script>
import api from '@/api/api.service'
import auth from '@/authConfig.js'
import PageHeaderPortal from '@/views/PageHeaderPortal.vue'

// GSWebAPIv2 serializes JSON via a custom LowerCaseNamingPolicy (Startup.cs) — "LaunchUrl"
// comes back as "launchurl", not "launchUrl" or "LaunchUrl" — on top of which LoanPriceSheet.vue
// shows other endpoints have been seen in camelCase/PascalCase too. Rather than enumerate every
// casing per field, match keys by a normalized form (lowercased, separators stripped) so any of
// those conventions resolves the same way.
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  const normalize = k => String(k).toLowerCase().replace(/[_-]/g, '')
  const wanted = keys.map(normalize)
  for (const actualKey of Object.keys(obj)) {
    if (wanted.includes(normalize(actualKey)) && obj[actualKey] !== undefined && obj[actualKey] !== null) {
      return obj[actualKey]
    }
  }
  return undefined
}

export default {
  name: 'PollyPricerView',
  components: {
    PageHeaderPortal
  },
  props: {
    // From the route. Absent = Scenario Mode; present = Lock Mode for that loan.
    // This is the SWIFT loan GUID (row.id in the pipeline grid) — the same id already used
    // for Polly's own Loan Service calls elsewhere (price sheet / reprice-pricing).
    loanId: { type: String, default: null }
  },
  data() {
    return {
      loading: true,
      error: '',
      launchUrl: '',
      pollyUsername: ''
    }
  },
  async mounted() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = ''

      try {
        const tokenResponse = await auth.acquireTokenResponse()

        // azure_id is the SPA's own "acting user" id — authConfig.js's substitueImpersonation
        // already swaps it to the impersonated user's id when a support/admin is impersonating
        // someone, so this is the one identifier that's always correct either way. The backend
        // resolves the actual email from it (AzureUsersData) rather than us sending an email
        // directly, which would otherwise always be the real signed-in MSAL account even while
        // impersonating.
        const azureId = localStorage.getItem('azure_id') || null

        if (!azureId) {
          throw new Error('Could not determine your signed-in identity. Try signing out and back in.')
        }

        const query = this.$route.query || {}

        const request = {
          azureId,
          loanId: this.loanId || null,
          audienceId: query.audienceId || null,
          quoteScenarioId: query.quoteScenarioId || null
        }

        const response = await api.GetPollyPortalLaunchUrl(request, tokenResponse.accessToken)

        const wrapper = response?.data || {}
        const result = pick(wrapper, 'data', 'Data') || {}

        const isSuccess = pick(wrapper, 'isSuccess', 'issuccess', 'IsSuccess')
        const message = pick(wrapper, 'message', 'Message') || ''
        const launchUrl = pick(result, 'launchUrl', 'LaunchUrl')

        if (!isSuccess || !launchUrl) {
          throw new Error(message || 'The Pricing Engine did not return a launch URL.')
        }

        this.launchUrl = launchUrl
        this.pollyUsername = pick(result, 'pollyUsername', 'PollyUsername') || ''
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          'Unable to open the Pricing Engine.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.polly-pricer-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-icon {
  width: 48px; height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}
.page-title  { margin: 0; font-size: 20px; font-weight: 700; color: #1f2a44; letter-spacing: -.01em; }

.polly-pricer-frame {
  flex: 1 1 auto;
  width: 100%;
  border: none;
}

.state-card {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7b8397;
  font-size: 14px;
}

.blocked-card {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: left;
}
.blocked-icon { font-size: 32px; color: #c0392b; flex: 0 0 auto; }
.blocked-title { font-size: 16px; font-weight: 700; color: #1f2a44; margin-bottom: 4px; }
.blocked-text { font-size: 13px; color: #5b6478; }
</style>
